import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from './Buttons';
import Table from './Tables';

import { useAlert } from './AlertContext';
import { useConfirmation } from './ConfirmationContext';
import { useInlineSkillApi } from '../Services/InlineSkillApiProvider';
import { InlineSkillApi, InlineSkillApiList } from '../Services/InlineSkillApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function InlineSkillsTable({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getInlineSkills, deleteInlineSkill } = useInlineSkillApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [inlineSkills, setInlineSkills] = useState<InlineSkillApi[]>([]);
  const [totalInlineSkills, setTotalInlineSkills] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const tableTitle = 'Inline Skills';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const inlineSkillUrl = (inlineSkillKey: string) =>
    `/organizations/${organizationId}/prompts-hub/inline-skills/${inlineSkillKey}`;

  const columns = [
    { name: 'ID', selector: (row: InlineSkillApi) => row.key, sortable: false },
    { name: 'Name', selector: (row: InlineSkillApi) => row.name, sortable: false },
    {
      name: 'Preloaded',
      selector: (row: InlineSkillApi) => (row.is_preloaded ? 'Yes' : 'No'),
      sortable: false,
    },
    {
      name: 'Active',
      selector: (row: InlineSkillApi) => (row.is_active ? 'Yes' : 'No'),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: InlineSkillApi) => formatDateTimeLocal(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: InlineSkillApi) => formatDateTimeLocal(row.updated_at),
      sortable: false,
    },
  ];

  const fetchInlineSkills = async (
    skip: number,
    limit: number,
    search: string = searchQuery,
  ): Promise<void> => {
    setLoading(true);

    try {
      const response: InlineSkillApiList = await getInlineSkills(
        skip,
        limit,
        search.trim() || undefined,
      );
      setInlineSkills(response.items);
      setTotalInlineSkills(response.totalCount);
    } catch (error) {
      console.error('Error fetching inline skills:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (skip: number, limit: number, search: string) => {
    setSearchQuery(search);
    fetchInlineSkills(skip, limit, search);
  };

  const handleDelete = async (selectedKeys: string[]): Promise<void> => {
    if (!selectedKeys[0]) return;
    showConfirmation('Are you sure you want to delete the selected inline skill?', async () => {
      try {
        setLoading(true);
        await deleteInlineSkill(selectedKeys[0]);
        fetchInlineSkills(storedSkip(), storedLimit());
        showAlert('success', 'Inline skill was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the inline skill');
      } finally {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchInlineSkills(storedSkip(), storedLimit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <Table
        title={tableTitle}
        columns={columns}
        data={inlineSkills}
        selectableRows
        selectableRowsHighlight
        progressPending={loading}
        paginationPerPage={initialPageSize}
        paginationTotalRows={totalInlineSkills}
        disabled={false}
        onRowClicked={(row: InlineSkillApi) => navigate(inlineSkillUrl(row.key))}
        onDelete={handleDelete}
        onChangePage={fetchInlineSkills}
        typeSearch="default"
        searchOnEnter
        onChangeFilter={onSearch}
        getRowUrl={(row: InlineSkillApi) => inlineSkillUrl(row.key)}
      />
      {canEdit && (
        <Button
          background="color2"
          size="large"
          onClick={() => navigate(inlineSkillUrl('new-inline-skill'))}
        >
          Create New Inline Skill
        </Button>
      )}
    </>
  );
}

export default InlineSkillsTable;
